<template>
  <div class="my-2 rounded-lg border">
    <div class="my-2 grid grid-cols-1 gap-4 p-2 sm:grid-cols-6 md:col-span-2">
      <StdPropEditor
        v-for="prop in salesForceIntegrationProps"
        :key="prop.name"
        :prop-def="prop"
        :class="[getPropLayoutClasses(prop)]"
        :model-value="configurationData[prop.name]"
        @update:model-value="
          (newValue) => {
            onUpdateProp(prop, newValue);
          }
        "
      />
    </div>
    <ToggleGroup v-model="advancedEnabled" name="Advanced" description="" class="my-2 grid grid-cols-1 gap-4 p-2 sm:grid-cols-1 md:col-span-2">
      <div class="col-span-full mt-2 border-l-2 border-indigo-100 pl-4">
        <StdPropEditor
          v-for="prop in salesForceAdvancedIntegrationProps"
          :key="prop.name"
          :prop-def="prop"
          :class="[getPropLayoutClasses(prop)]"
          :model-value="configurationData[prop.name]"
          @update:model-value="
            (newValue) => {
              onUpdateProp(prop, newValue);
            }
          "
        />
      </div>
    </ToggleGroup>
    <Message v-if="errorMessage !== ''" class="m-2" severity="error" :sticky="false">{{ errorMessage }}</Message>
    <ProgressDialog ref="integrationsOpProgress"></ProgressDialog>
    <div class="m-2 flex items-center justify-end">
      <button
        type="button"
        class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
        @click="onCancel"
      >
        Cancel
      </button>
      <button type="button" class="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto" @click="onSave">Save</button>
    </div>
  </div>
</template>
<script setup>
  import { ref, reactive, onMounted } from 'vue';
  import StdPropEditor from '@/components/Editors/StdPropEditor.vue';
  import { useApi } from '@/hooks/useApi';
  import { useSettingsStore } from '@/store/SettingsStore';
  import Message from 'primevue/message';
  import { useToast } from 'vue-toastification';
  import ProgressDialog from '@/components/Dialogs/ProgressDialog.vue';
  import ToggleGroup from '@/components/Toggles/ToggleGroup.vue';

  const emit = defineEmits(['canceled', 'saved']);
  const advancedEnabled = ref(false);

  const settingsStore = useSettingsStore();

  const errorMessage = ref('');
  const integrationsOpProgress = ref(null);
  const toast = useToast();

  let configurationData = reactive({
    consumerKey: '',
    consumerSecret: '',
    subdomain: '',
    envType: 'production',
    externalUrl: '',
    messageApiOpts: '',
  });

  const salesForceIntegrationProps = [
    { name: 'consumerKey', type: 'string', title: 'Consumer Key', default: '' },
    { name: 'consumerSecret', type: 'string', title: 'Consumer Secret', default: '' },
    { name: 'subdomain', type: 'string', title: 'Domain', default: '' },
    {
      name: 'envType',
      type: 'select',
      title: 'Salesforce Environment Type',
      options: ['Production', 'Development', 'Sandbox'],
      values: ['production', 'develop', 'sandbox'],
      default: 'production',
    },
    {
      name: 'messageApiOpts',
      type: 'text',
      rows: 5,
      title: 'Messaging API Client Code Snippet',
      default: '',
      placeholder: '{\n\t"OrganizationId": "00D1s0000000xxx",\n\t"DeveloperName": "myDeployment",\n\t"Url": "https://mycompanyname.salesforce-scrt.com"\n}',
    },
  ];
  const salesForceAdvancedIntegrationProps = [{ name: 'externalUrl', type: 'string', title: 'Outbound Webhook URL', placeholder: 'Enter a custom URL or leave blank to use the default.', default: '' }];

  const propLayoutClassesByType = {
    text: 'col-span-full w-full',
    boolean: 'mt-5 sm:col-span-full md:col-span-full lg:col-span-3 xl:col-span-3 flex items-center justify-start',
    number: 'sm:col-span-full md:col-span-full lg:col-span-3 xl:col-span-3',
    string: 'sm:col-span-full md:col-span-full lg:col-span-3 xl:col-span-3',
    select: 'sm:col-span-full md:col-span-full lg:col-span-3 xl:col-span-3',
    stringLong: 'col-span-full',
    audioFile: 'col-span-full',
    transcriptsCollection: 'col-span-full',
    recordingsCollection: 'col-span-full',
    kpiCollection: 'col-span-full',
    categoriesCollection: 'col-span-full',
    temperature: 'col-span-full',
  };

  function getPropLayoutClasses(prop) {
    return propLayoutClassesByType[prop?.type] || '';
  }

  function onUpdateProp(prop, newValue) {
    console.log(`onUpdateProp: ${prop.name} = "${newValue}"`);
    configurationData[prop.name] = newValue;
    //emit('update:modelValue', arrayValue);
  }

  function validateConfiguration() {
    if (!configurationData.consumerKey || configurationData.consumerKey === '') {
      return {
        valid: false,
        message: 'Please provide value for Consumer Key',
      };
    }
    if (!configurationData.consumerSecret || configurationData.consumerSecret === '') {
      return {
        valid: false,
        message: 'Please provide value for Consumer Secret',
      };
    }
    if (!configurationData.subdomain || configurationData.subdomain === '') {
      return {
        valid: false,
        message: 'Please provide value for Domain',
      };
    }

    return { valid: true };
  }

  async function onSave() {
    errorMessage.value = '';
    const validateResult = validateConfiguration();
    if (!validateResult.valid) {
      errorMessage.value = validateResult.message;
      return;
    }

    // extract subdomain, if full Portal URL provided
    configurationData.subdomain = configurationData.subdomain.replace(/^https?:\/\//, '').split('.')[0];

    const api = useApi();

    integrationsOpProgress.value.show({
      title: 'Enabling Salesforce Integration',
      description: `Validating Credentials`,
    });

    // Validate Credentials and provision account
    let provisionAccountResult = await api.salesForceProvisionAccount(configurationData);

    // Helper function to truncate a string to maxLength characters
    const truncate = (str, maxLength = 50) => (str.length > maxLength ? str.slice(0, maxLength) + '…' : str);

    if (!provisionAccountResult.success) {
      integrationsOpProgress.value.showError({ message: `${provisionAccountResult.error}` });
      return;
    }

    integrationsOpProgress.value.show({
      title: 'Enabling Salesforce Integration',
      description: `Creating Resources`,
    });

    let provisioningResult = await api.salesForceProvisionResources(configurationData.externalUrl);

    const deploymentId = provisioningResult.id || '';
    const deploymentStatus = provisioningResult.status || '';
    const deploymentError = provisioningResult.error ? `\n${provisioningResult.error}` : '';
    const isSuccess = provisioningResult.success || false;

    toast[isSuccess ? 'success' : 'error'](`Deployment ID: ${deploymentId}\nStatus: ${deploymentStatus}${deploymentError}`, { timeout: 10000 });

    integrationsOpProgress.value.hide();

    // Enable Salesforce Integration
    await settingsStore.setIntegrationEnabled('salesforce', true);
    toast.success(`Salesforce Integration Enabled.`);
  }

  function onCancel() {
    emit('canceled');
  }

  onMounted(async () => {
    const config = await settingsStore.getIntegrationSettings('salesforce');
    if (!config || typeof config !== 'object') {
      return;
    }
    Object.keys(config).map((x) => {
      configurationData[x] = config[x];
    });
  });
</script>
